import {
  ClipboardList,
  Clock,
  CornerDownLeft,
  FileText,
  Handshake,
  History,
  ImageOff,
  LayoutGrid,
  type LucideIcon,
  PackageCheck,
  Plus,
  Search,
  Store,
  UserRound,
  X,
} from "lucide-react";
import { Dialog } from "radix-ui";
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useAuth } from "@/auth/use-auth";
import { Money } from "@/components/Money";
import { StatusChip } from "@/components/StatusChip";
import { ConditionBadge } from "@/features/products/ConditionBadge";
import { productStatusTone } from "@/features/products/status";
import { cn } from "@/lib/utils";

import {
  loadOpened,
  loadQueries,
  type RecentResult,
  rememberOpened,
  rememberQuery,
} from "./history";
import { type SearchGroupKey, type SearchItem, useGlobalSearch } from "./useGlobalSearch";

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Text to start with (a scanned code, the header button's current text). */
  initialQuery?: string;
  /** A scan: open the product right away when the code finds exactly one. */
  autoOpenExact?: boolean;
}

const GROUP_ICON: Readonly<Record<SearchGroupKey, LucideIcon>> = {
  lookup: CornerDownLeft,
  actions: LayoutGrid,
  products: LayoutGrid,
  orders: ClipboardList,
  customers: UserRound,
  consignors: Handshake,
  pages: LayoutGrid,
};

const ACTION_ICON: Readonly<Record<string, LucideIcon>> = {
  add: Plus,
  ship: PackageCheck,
  pos: Store,
  orders: ClipboardList,
  content: FileText,
};

type Row =
  | { kind: "item"; item: SearchItem; group: SearchGroupKey }
  | { kind: "query"; query: string }
  | { kind: "recent"; recent: RecentResult };

function rowId(prefix: string, row: Row): string {
  const raw =
    row.kind === "item"
      ? row.item.id
      : row.kind === "query"
        ? `q:${row.query}`
        : `r:${row.recent.to}`;
  return `${prefix}-${raw.replace(/[^A-Za-z0-9_-]/g, "_")}`;
}

function ItemBody({ item }: { item: SearchItem }) {
  const { t } = useTranslation(["search", "common", "products"]);
  if (item.type === "product") {
    const status = productStatusTone(item.status);
    return (
      <>
        <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded border bg-muted">
          {item.imageUrl ? (
            <img src={item.imageUrl} alt="" className="size-full object-cover" loading="lazy" />
          ) : (
            <ImageOff className="size-4 text-muted-foreground" aria-hidden />
          )}
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-medium">{item.title}</span>
          {item.subtitle ? (
            <span className="truncate text-xs text-muted-foreground">{item.subtitle}</span>
          ) : null}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <ConditionBadge
            size="sm"
            source={{
              productType: item.productType,
              conditionLabel: item.condition,
              rawRank: item.condition && /^[ABCD]$/.test(item.condition) ? item.condition : null,
              gradeLabel: item.productType === "graded_single" ? item.condition : null,
            }}
          />
          {item.price ? <Money value={item.price} className="text-sm font-semibold" /> : null}
          <StatusChip
            tone={status.tone}
            label={t(status.labelKey)}
            className="hidden sm:inline-flex"
          />
        </span>
      </>
    );
  }
  if (item.type === "order") {
    return (
      <>
        <ClipboardList className="size-5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-medium">{item.title}</span>
          <span className="truncate text-xs text-muted-foreground">
            {[
              item.subtitle,
              t(`common:order_status.${item.status}`, { defaultValue: "" }),
              t(`common:fulfillment_status.${item.fulfillmentStatus}`, { defaultValue: "" }),
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </span>
        {item.total ? (
          <Money value={item.total} className="shrink-0 text-sm font-semibold" />
        ) : null}
      </>
    );
  }
  const Icon =
    item.type === "action"
      ? (ACTION_ICON[item.icon] ?? LayoutGrid)
      : item.type === "customer"
        ? UserRound
        : item.type === "consignor"
          ? Handshake
          : LayoutGrid;
  return (
    <>
      <Icon className="size-5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium">{item.title}</span>
        {item.subtitle ? (
          <span className="truncate text-xs text-muted-foreground">{item.subtitle}</span>
        ) : null}
      </span>
      {item.external ? (
        <span className="shrink-0 text-xs text-muted-foreground">{t("search:opens_new_tab")}</span>
      ) : null}
    </>
  );
}

/**
 * Global search (T11-07, design/04 §0/§1/§15): ⌘K / Ctrl+K / "/" / the header button / a scan.
 * An accessible combobox (input + listbox, ↑↓ Enter Esc); a scanned or typed cert / item code
 * opens its product with Enter as soon as the lookup has exactly one answer.
 */
export function CommandPalette({
  open,
  onOpenChange,
  initialQuery = "",
  autoOpenExact = false,
}: CommandPaletteProps) {
  const { t } = useTranslation(["search", "common"]);
  const navigate = useNavigate();
  const { session } = useAuth();
  const staffId = session?.user.id ?? "anonymous";
  const baseId = useId().replace(/:/g, "");
  const listId = `${baseId}-list`;
  const inputRef = useRef<HTMLInputElement>(null);
  const composing = useRef(false);

  // mounted per opening (SearchProvider): state starts from the props, history is read once
  const [query, setQueryState] = useState(initialQuery);
  const [active, setActive] = useState(0);
  const waitingEnter = useRef(autoOpenExact);
  const [recentQueries, setRecentQueries] = useState<string[]>(() => loadQueries(staffId));
  const [recentOpened] = useState<RecentResult[]>(() => loadOpened(staffId));
  const setQuery = (next: string) => {
    setQueryState(next);
    setActive(0);
  };

  const search = useGlobalSearch(query, open);

  // most searches open a product: fetch the product form's code while the staff types, so a scan
  // + Enter lands on the product without waiting for the screen to download
  useEffect(() => {
    if (open) {
      void import("@/features/products/edit/ProductEditPage");
    }
  }, [open]);

  const rows: Row[] = useMemo(() => {
    if (search.kind === "empty") {
      return [
        ...search.groups.flatMap((g) =>
          g.items.map((item) => ({ kind: "item" as const, item, group: g.key })),
        ),
        ...recentOpened.map((recent) => ({ kind: "recent" as const, recent })),
        ...recentQueries.map((q) => ({ kind: "query" as const, query: q })),
      ];
    }
    return search.groups.flatMap((g) =>
      g.items.map((item) => ({ kind: "item" as const, item, group: g.key })),
    );
  }, [search.kind, search.groups, recentOpened, recentQueries]);

  const openTarget = useCallback(
    (target: { to: string; external?: boolean; title: string; kind: RecentResult["kind"] }) => {
      if (search.term.length >= 2 && search.kind !== "empty") {
        rememberQuery(staffId, search.term);
      }
      onOpenChange(false);
      if (target.external) {
        window.open(target.to, "_blank", "noopener");
        return;
      }
      rememberOpened(staffId, { to: target.to, title: target.title, kind: target.kind });
      void navigate(target.to);
    },
    [navigate, onOpenChange, search.kind, search.term, staffId],
  );

  const choose = useCallback(
    (row: Row) => {
      if (row.kind === "query") {
        setQuery(row.query);
        inputRef.current?.focus();
        return;
      }
      if (row.kind === "recent") {
        openTarget({ ...row.recent });
        return;
      }
      const item = row.item;
      openTarget({
        to: item.to,
        external: item.external,
        // a customer without a name is shown by e-mail — history keeps a neutral word instead
        title:
          item.type === "customer" && !item.subtitle ? t("search:groups.customers") : item.title,
        kind: item.type,
      });
    },
    [openTarget, t],
  );

  // Enter pressed (or a scan) while the code lookup was still on its way
  useEffect(() => {
    if (!waitingEnter.current || search.lookupPending || search.kind !== "code") {
      return;
    }
    waitingEnter.current = false;
    if (search.exactMatch) {
      openTarget({ to: search.exactMatch.to, title: search.exactMatch.title, kind: "product" });
    }
  }, [search.lookupPending, search.exactMatch, search.kind, openTarget]);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing || composing.current || event.keyCode === 229) {
      return; // the IME uses Enter / arrows to pick kanji
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((i) => (rows.length ? (i + 1) % rows.length : 0));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => (rows.length ? (i - 1 + rows.length) % rows.length : 0));
    } else if (event.key === "Home") {
      setActive(0);
    } else if (event.key === "End") {
      setActive(Math.max(0, rows.length - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (search.kind === "code" && search.lookupPending) {
        waitingEnter.current = true;
        return;
      }
      if (search.exactMatch && active === 0) {
        openTarget({ to: search.exactMatch.to, title: search.exactMatch.title, kind: "product" });
        return;
      }
      const row = rows[active];
      if (row) {
        choose(row);
      } else if (search.term.length >= 2) {
        setRecentQueries(rememberQuery(staffId, search.term));
      }
    }
  };

  const activeRow = rows[active];
  const activeId = activeRow ? rowId(baseId, activeRow) : undefined;
  const anyLoading = search.loading;
  const nothing =
    search.kind !== "empty" && !anyLoading && rows.filter((r) => r.kind === "item").length === 0;

  let index = -1;
  const renderRow = (row: Row) => {
    index += 1;
    const i = index;
    const id = rowId(baseId, row);
    const selected = i === active;
    return (
      <li
        key={id}
        id={id}
        role="option"
        aria-selected={selected}
        data-kind={row.kind === "item" ? row.item.type : row.kind}
        onMouseMove={() => {
          if (!selected) {
            setActive(i);
          }
        }}
        onClick={() => {
          choose(row);
        }}
        className={cn(
          "flex min-h-touch cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm",
          selected ? "bg-accent text-accent-foreground" : "hover:bg-accent/60",
        )}
      >
        {row.kind === "item" ? (
          <ItemBody item={row.item} />
        ) : row.kind === "recent" ? (
          <>
            <History className="size-5 shrink-0 text-muted-foreground" aria-hidden />
            <span className="min-w-0 flex-1 truncate">{row.recent.title}</span>
          </>
        ) : (
          <>
            <Clock className="size-5 shrink-0 text-muted-foreground" aria-hidden />
            <span className="min-w-0 flex-1 truncate">{row.query}</span>
          </>
        )}
      </li>
    );
  };

  const sections: Array<{
    key: string;
    label: string;
    rows: Row[];
    loading?: boolean;
    error?: boolean;
  }> = [];
  for (const group of search.groups) {
    sections.push({
      key: group.key,
      label: t(`search:groups.${group.key}`),
      rows: group.items.map((item) => ({ kind: "item", item, group: group.key })),
      loading: group.loading,
      error: group.error,
    });
  }
  if (search.kind === "empty") {
    if (recentOpened.length) {
      sections.push({
        key: "recent",
        label: t("search:groups.recent_opened"),
        rows: recentOpened.map((recent) => ({ kind: "recent", recent })),
      });
    }
    if (recentQueries.length) {
      sections.push({
        key: "queries",
        label: t("search:groups.recent_queries"),
        rows: recentQueries.map((q) => ({ kind: "query", query: q })),
      });
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] bg-black/40" />
        <Dialog.Content
          data-slot="command-palette"
          aria-describedby={undefined}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            inputRef.current?.focus();
            inputRef.current?.select();
          }}
          className="fixed inset-x-0 top-0 z-[71] flex max-h-dvh flex-col border-b bg-background shadow-lg sm:inset-x-auto sm:top-[10vh] sm:left-1/2 sm:max-h-[75vh] sm:w-[min(40rem,calc(100vw-2rem))] sm:-translate-x-1/2 sm:rounded-lg sm:border"
        >
          <Dialog.Title className="sr-only">{t("search:title")}</Dialog.Title>
          <div className="flex items-center gap-2 border-b px-3">
            <Search className="size-5 shrink-0 text-muted-foreground" aria-hidden />
            <input
              ref={inputRef}
              role="combobox"
              aria-expanded={rows.length > 0}
              aria-controls={listId}
              aria-activedescendant={activeId}
              aria-autocomplete="list"
              aria-label={t("search:input_label")}
              placeholder={t("search:placeholder")}
              value={query}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="search"
              onChange={(event) => {
                setQuery(event.target.value);
              }}
              onCompositionStart={() => {
                composing.current = true;
              }}
              onCompositionEnd={() => {
                composing.current = false;
              }}
              onKeyDown={onKeyDown}
              className="min-h-14 min-w-0 flex-1 bg-transparent text-base outline-none"
            />
            {anyLoading ? (
              <span role="status" className="text-xs text-muted-foreground">
                {t("search:searching")}
              </span>
            ) : null}
            <Dialog.Close
              className="inline-flex size-touch shrink-0 items-center justify-center rounded-md hover:bg-accent"
              aria-label={t("search:close")}
            >
              <X className="size-5" aria-hidden />
            </Dialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            <ul
              id={listId}
              role="listbox"
              aria-label={t("search:results")}
              className="flex flex-col gap-1"
            >
              {sections.map((section) => {
                const Icon = GROUP_ICON[section.key as SearchGroupKey] ?? LayoutGrid;
                return (
                  <li key={section.key} role="presentation" className="flex flex-col gap-0.5">
                    <div
                      role="presentation"
                      className="flex items-center gap-2 px-3 pt-2 pb-1 text-xs font-semibold text-muted-foreground"
                    >
                      <Icon className="size-3.5" aria-hidden />
                      {section.label}
                      {section.loading ? <span>· {t("search:searching")}</span> : null}
                      {section.error ? (
                        <span className="text-status-problem">· {t("search:source_error")}</span>
                      ) : null}
                    </div>
                    <ul role="group" aria-label={section.label} className="flex flex-col gap-0.5">
                      {section.rows.map(renderRow)}
                    </ul>
                  </li>
                );
              })}
            </ul>
            {nothing ? (
              <div role="status" className="flex flex-col gap-1 px-3 py-6 text-sm">
                <p className="font-medium">{t("search:nothing", { term: search.term })}</p>
                <p className="text-muted-foreground">{t("search:nothing_hint")}</p>
              </div>
            ) : null}
            {search.kind === "empty" && sections.length === 0 ? (
              <p className="px-3 py-6 text-sm text-muted-foreground">{t("search:empty_hint")}</p>
            ) : null}
          </div>
          <p className="hidden border-t px-3 py-2 text-xs text-muted-foreground sm:block">
            {t("search:keys_hint")}
          </p>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
